/**
 * Asset discovery orchestrator.
 *
 * Walks a directory tree, identifies asset manifests via deterministic
 * filename matching, parses each manifest, computes file filters, and
 * extracts the requires graph.
 *
 * Zero storage dependencies — reused by LocalCatalogSource (Phase 1) and
 * the store backend (Phase 2).
 */

import * as fs from "node:fs";
import * as path from "node:path";
import { parseFrontmatter } from "@skaile/workspaces/core/manifest";
import { isValidAssetName } from "@skaile/workspaces/core/models";

import type { AssetKindFileFilter, IAssetKindRegistry } from "@skaile/workspaces/plugins";
import * as yaml from "js-yaml";
import { AssetKindRegistry } from "./asset-kind-registry.js";
import { BUILTIN_PROVIDERS } from "./builtin-providers.js";
import { discoverFromManifest } from "./discover-manifest.js";
import {
  applyDomains,
  type DomainIndexEntry,
  type DomainMd,
  isDomainMdPath,
  parseDomainMd,
} from "./domains.js";
import { computeDeterministicHash } from "./hash.js";
import {
  buildSkillNameToRefs,
  closeOverReferences,
  createNodeAssetFs,
  lintUncoveredReferences,
  pushBodyLinkEdge,
  type ReferenceWarning,
  resolveSlashCommand,
  scanAssetReferences,
  scanSlashCommandNames,
} from "./reference-lint.js";
import { buildRequiresGraph, type RequiresEdge, type RequiresGraph } from "./requires-graph.js";
import type { SourceConfig } from "./source-config.js";
import {
  barePublisher,
  isExcludedDevPath,
  isManifestMode,
  loadMergedSourceConfig,
  loadMergedSourceConfigFromFile,
} from "./source-config.js";

export { computeDeterministicHash } from "./hash.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * How aggressively a discovered asset's byte set is closed over its references.
 *
 * - `strict`   — structural filter only; uncovered in-dir references are
 *                reported as warnings but NOT pulled in (default).
 * - `closure`  — structural filter + reference closure: in-dir files the asset
 *                textually references are pulled in transitively.
 * - `full-dir` — capture the entire asset directory (minus nested sub-assets).
 *
 * All three are mirrored byte-for-byte by the virtual-tree (store) discovery
 * path, so store/local SHA256 parity is exact under every policy.
 *
 * @docLink packages/discovery/concepts#atomization
 */
export type AtomizationPolicy = "strict" | "closure" | "full-dir";

/**
 * Default atomization policy. `strict` keeps the byte set to the structural
 * filter (never silently bundling referenced-but-uncaptured files); closure and
 * full-dir are opt-in and expand it. All three hold store/local parity.
 */
export const DEFAULT_ATOMIZATION_POLICY: AtomizationPolicy = "strict";

/**
 * A single asset discovered during a {@link discoverAssetsInTree} run, including its parsed
 * manifest, file filter, deterministic hash, and cross-asset dependency edges.
 *
 * @docLink packages/discovery/concepts#discovered-asset
 */
export interface DiscoveredAsset {
  /** Canonical ref: `<publisher>/<name>@<version>`. */
  ref: string;
  /** Asset kind (from registry resolution). */
  kind: string;
  publisher: string;
  name: string;
  version: string;
  /** Parsed manifest (frontmatter for .md, full YAML for .yaml/.json). */
  manifest: Record<string, unknown>;
  /** Absolute path to the manifest file. */
  manifestPath: string;
  /** Relative path from source root. */
  relativePath: string;
  /** Files included in this asset's tarball. */
  fileFilter: AssetKindFileFilter;
  /** SHA256 hash of the deterministic tarball content. */
  sha256: string;
  /** Cross-asset dependency edges originating from this asset. */
  requires: RequiresEdge[];
  /**
   * Navigational domain slug assigned from a sidecar `DOMAIN.md`, or `undefined`
   * when no domain claims this asset. Display-only — never part of identity.
   *
   * @docLink packages/discovery/concepts#domains
   */
  domainSlug?: string;
}

export interface DiscoveryOptions {
  /** Optional source config (adapted from `skaile.manifest.yaml`) for publisher resolution. */
  sourceConfig?: SourceConfig;
  /**
   * Optional sidecar directory (e.g. `~/.skaile/sources/<slug>/`) carrying a
   * local overlay `skaile.manifest.yaml`. When set and `sourceConfig` is
   * `undefined`, the helper loads the base in-repo config from `rootPath` and
   * merges the sidecar overlay on top. When both `sourceConfig` and
   * `sidecarPath` are set, the explicit `sourceConfig` wins (the caller is
   * responsible for merging).
   *
   * @docLink packages/discovery/concepts#sidecar-overlay
   */
  sidecarPath?: string;
  /**
   * Explicit overlay manifest FILE path (e.g. the local store's flat
   * `~/.skaile/store/manifests/<slug>.yaml`). When set and `sourceConfig` is
   * `undefined`, the base config from `rootPath` is merged with this overlay via
   * {@link loadMergedSourceConfigFromFile}. Takes precedence over `sidecarPath`
   * (the dir convention) when both are set.
   *
   * @docLink packages/discovery/concepts#sidecar-overlay
   */
  sidecarManifestFile?: string;
  /**
   * Explicit `DOMAIN.md` descriptors (path relative to the repo root + raw
   * content). When provided, these are the authoritative domain source and the
   * in-tree / sidecar `DOMAIN.md` scan is skipped — the store supplies these
   * from a sidecar repo. Paths resolve against the upstream tree.
   *
   * @docLink packages/discovery/concepts#domains
   */
  domainFiles?: Array<{ path: string; content: string }>;
  /** Optional kind registry. If omitted, a default registry with all built-in providers is used. */
  registry?: IAssetKindRegistry;
  /**
   * Include assets under the source config's `dev_paths` prefixes. Defaults to
   * `false` — a normal run excludes dev-only assets (e.g. `ai-assets-dev/`).
   * Set by the `--dev` flag on `skaile source sync` / `source add`.
   *
   * @docLink packages/discovery/concepts#dev-paths
   */
  includeDev?: boolean;
  /**
   * Atomization policy controlling how an asset's byte set is derived from the
   * structural filter. Defaults to {@link DEFAULT_ATOMIZATION_POLICY}.
   *
   * @docLink packages/discovery/concepts#atomization
   */
  atomization?: AtomizationPolicy;
}

/**
 * The complete output of a {@link discoverAssetsInTree} scan: all discovered assets, the
 * full requires graph across those assets, and any non-fatal parse errors encountered.
 *
 * @docLink packages/discovery/concepts#discovery-result
 */
export interface DiscoveryResult {
  assets: DiscoveredAsset[];
  graph: RequiresGraph;
  errors: Array<{ path: string; error: string }>;
  /**
   * Non-fatal advisories, `{ path, warning }` per entry. Two sources feed this
   * channel: (1) atomization warnings — an asset reference that points at a real
   * file/directory inside the asset's own directory yet was dropped by the
   * structural file filter (see {@link ./reference-lint.ts}); (2) author-shipped
   * manifest advisories — an unknown asset kind (ghost-install) or a missing
   * `sha256` (auto-computed locally). Advisory only: identity, hashes, and the
   * captured byte set are unchanged.
   *
   * @docLink packages/discovery/concepts#atomization
   */
  warnings: ReferenceWarning[];
  /**
   * Resolved navigational domains (from sidecar `DOMAIN.md`), ordered for
   * display. Absent when no `DOMAIN.md` was supplied.
   *
   * @docLink packages/discovery/concepts#domains
   */
  domains?: DomainIndexEntry[];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create a default AssetKindRegistry with all built-in providers registered.
 * Used when no registry is passed to discoverAssetsInTree().
 */
export function createDefaultRegistry(): AssetKindRegistry {
  const registry = new AssetKindRegistry();
  for (const provider of BUILTIN_PROVIDERS) {
    registry.register(provider);
  }
  return registry;
}

/**
 * Discover all assets in a directory tree.
 *
 * Walks `rootPath` recursively, identifies manifests by filename, parses
 * them, computes file filters and SHA256 hashes, and extracts the
 * requires graph.
 *
 * @param rootPath - Absolute path to the directory tree to scan
 * @param optionsOrSourceConfig - Discovery options, or a SourceConfig for backward compat
 * @returns A {@link DiscoveryResult} with all discovered assets, the requires graph, and any parse errors
 * @docLink packages/discovery/concepts#discover-assets-in-tree
 */
export function discoverAssetsInTree(
  rootPath: string,
  optionsOrSourceConfig?: DiscoveryOptions | SourceConfig,
): DiscoveryResult {
  const { sourceConfig, registry, includeDev, sidecarPath, domainFiles, atomization } =
    normalizeDiscoveryArgs(rootPath, optionsOrSourceConfig);

  // Domain descriptors: explicit `domainFiles` win; otherwise scan the sidecar
  // dir (when set), else the repo root. Paths resolve against the upstream tree.
  const domainMds = domainFiles
    ? domainFiles.map((f) => parseDomainMd(f.path, parseDomainFrontmatter(f.content)))
    : collectDomainMds(sidecarPath ?? rootPath);

  // Dispatch: if the source config carries an author-shipped inventory,
  // discover from the manifest and skip the glob walk entirely.
  if (sourceConfig && isManifestMode(sourceConfig)) {
    return finalizeDomains(
      discoverFromManifest(rootPath, sourceConfig, registry, includeDev),
      domainMds,
    );
  }

  const assets: DiscoveredAsset[] = [];
  const errors: Array<{ path: string; error: string }> = [];
  const warnings: ReferenceWarning[] = [];
  const allEdges: RequiresEdge[] = [];
  const devPaths = sourceConfig?.dev_paths ?? [];

  // Dedup: for kinds where a directory might contain both agent.yaml and AGENT.md,
  // keep only the first match per directory+kind.
  const seen = new Set<string>();

  for (const { absPath, relativePath } of walkTree(rootPath)) {
    // Dev-only assets are excluded from a normal run; --dev re-includes them.
    if (!includeDev && isExcludedDevPath(relativePath, devPaths)) continue;

    // Resolve kind through the registry
    let kind: string | undefined;
    try {
      kind = registry.resolveKind(relativePath);
    } catch (err) {
      // AssetKindPathAmbiguityError — record and skip
      errors.push({
        path: relativePath,
        error: err instanceof Error ? err.message : String(err),
      });
      continue;
    }
    if (!kind) continue;

    // Dedup key: directory + kind
    const dir = path.dirname(relativePath);
    const dedupKey = `${dir}:${kind}`;
    if (seen.has(dedupKey)) continue;
    seen.add(dedupKey);

    processDiscoveredEntry(
      { absPath, relativePath, kind },
      { registry, sourceConfig, atomization },
      { assets, errors, warnings, allEdges },
    );
  }

  // Dependency post-pass: turn body links that point at ANOTHER discovered
  // asset into requires edges. Needs the full asset set to map a path → ref,
  // so it runs after the walk. Edges only — never changes any byte set.
  allEdges.push(...extractBodyLinkEdges(assets));

  const graph = buildRequiresGraph(allEdges);
  return finalizeDomains({ assets, graph, errors, warnings }, domainMds);
}

// ---------------------------------------------------------------------------
// Domains
// ---------------------------------------------------------------------------

/** Parse a `DOMAIN.md`'s YAML frontmatter into a plain object. */
function parseDomainFrontmatter(content: string): Record<string, unknown> {
  const { data } = parseFrontmatter(content);
  return data && typeof data === "object" ? (data as Record<string, unknown>) : {};
}

/** Scan a directory tree for `DOMAIN.md` files and parse each descriptor. */
function collectDomainMds(dir: string): DomainMd[] {
  const out: DomainMd[] = [];
  for (const { absPath, relativePath } of walkTree(dir)) {
    if (!isDomainMdPath(relativePath)) continue;
    try {
      out.push(
        parseDomainMd(relativePath, parseDomainFrontmatter(fs.readFileSync(absPath, "utf-8"))),
      );
    } catch {
      // unreadable DOMAIN.md — ignore (treated as absent)
    }
  }
  return out;
}

/** Stamp `domainSlug` on assets and attach the domain index to a result. */
function finalizeDomains(result: DiscoveryResult, domainMds: DomainMd[]): DiscoveryResult {
  if (domainMds.length === 0) return result;
  const { domains, errors } = applyDomains(result.assets, domainMds);
  result.errors.push(...errors);
  if (domains.length > 0) result.domains = domains;
  return result;
}

/**
 * Normalize {@link discoverAssetsInTree}'s overloaded second argument into the
 * resolved discovery inputs: the source config (after optional sidecar merge),
 * a guaranteed registry, and the `includeDev` flag.
 */
function normalizeDiscoveryArgs(
  rootPath: string,
  optionsOrSourceConfig?: DiscoveryOptions | SourceConfig,
): {
  sourceConfig: SourceConfig | undefined;
  registry: IAssetKindRegistry;
  includeDev: boolean;
  sidecarPath: string | undefined;
  sidecarManifestFile: string | undefined;
  domainFiles: Array<{ path: string; content: string }> | undefined;
  atomization: AtomizationPolicy;
} {
  // Backward-compat: accept a bare SourceConfig or the new options object
  let sourceConfig: SourceConfig | undefined;
  let registry: IAssetKindRegistry | undefined;
  let sidecarPath: string | undefined;
  let sidecarManifestFile: string | undefined;
  let includeDev = false;
  let domainFiles: Array<{ path: string; content: string }> | undefined;
  let atomization: AtomizationPolicy = DEFAULT_ATOMIZATION_POLICY;

  if (
    optionsOrSourceConfig &&
    ("registry" in optionsOrSourceConfig ||
      "sourceConfig" in optionsOrSourceConfig ||
      "sidecarPath" in optionsOrSourceConfig ||
      "sidecarManifestFile" in optionsOrSourceConfig ||
      "domainFiles" in optionsOrSourceConfig ||
      "atomization" in optionsOrSourceConfig ||
      "includeDev" in optionsOrSourceConfig)
  ) {
    sourceConfig = (optionsOrSourceConfig as DiscoveryOptions).sourceConfig;
    registry = (optionsOrSourceConfig as DiscoveryOptions).registry;
    sidecarPath = (optionsOrSourceConfig as DiscoveryOptions).sidecarPath;
    sidecarManifestFile = (optionsOrSourceConfig as DiscoveryOptions).sidecarManifestFile;
    includeDev = (optionsOrSourceConfig as DiscoveryOptions).includeDev ?? false;
    domainFiles = (optionsOrSourceConfig as DiscoveryOptions).domainFiles;
    atomization =
      (optionsOrSourceConfig as DiscoveryOptions).atomization ?? DEFAULT_ATOMIZATION_POLICY;
  } else {
    sourceConfig = optionsOrSourceConfig as SourceConfig | undefined;
  }

  // Use default registry if none provided
  if (!registry) {
    registry = createDefaultRegistry();
  }

  // Sidecar overlay: if no explicit sourceConfig was passed, load the base
  // config from rootPath and merge in the overlay before dispatching. An
  // explicit overlay FILE (the store's flat `<slug>.yaml`) takes precedence
  // over the sidecar DIR convention.
  if (!sourceConfig && (sidecarManifestFile || sidecarPath)) {
    const merged = sidecarManifestFile
      ? loadMergedSourceConfigFromFile(rootPath, sidecarManifestFile)
      : loadMergedSourceConfig(rootPath, sidecarPath);
    if (merged.ok) {
      sourceConfig = merged.config;
    }
  }

  return {
    sourceConfig,
    registry,
    includeDev,
    sidecarPath,
    sidecarManifestFile,
    domainFiles,
    atomization,
  };
}

/**
 * Process a single kind-resolved walk entry: parse its manifest, derive
 * identity, compute the file filter + hash, extract requires, and append the
 * resulting {@link DiscoveredAsset} (or a parse/processing error) to the
 * accumulators. A null/failed manifest or any thrown error is recorded in
 * `errors` and the entry is skipped.
 */
function processDiscoveredEntry(
  entry: { absPath: string; relativePath: string; kind: string },
  ctx: {
    registry: IAssetKindRegistry;
    sourceConfig: SourceConfig | undefined;
    atomization: AtomizationPolicy;
  },
  acc: {
    assets: DiscoveredAsset[];
    errors: Array<{ path: string; error: string }>;
    warnings: ReferenceWarning[];
    allEdges: RequiresEdge[];
  },
): void {
  const { absPath, relativePath, kind } = entry;
  const { registry, sourceConfig, atomization } = ctx;

  try {
    const manifest = parseManifest(absPath);
    if (!manifest) {
      acc.errors.push({ path: relativePath, error: "Failed to parse manifest" });
      return;
    }

    const publisher = resolvePublisher(relativePath, sourceConfig);
    const name = deriveName(relativePath, kind, manifest);
    const version = coerceVersion(manifest);
    // `publisher` is bare; the canonical ref carries the `@` scope sigil.
    const ref = `@${publisher}/${name}@${version}`;

    // Structural file filter — through registry.
    const fileFilter = registry.defaultFileFilter(kind, absPath) ?? {
      assetRoot: path.dirname(absPath),
      files: [path.basename(absPath)],
    };

    // Apply the atomization policy on top of the structural filter.
    applyAtomizationPolicy(atomization, relativePath, fileFilter, registry, acc.warnings);

    // SHA256 over the (possibly expanded) file set.
    const sha256 = computeDeterministicHash(fileFilter.assetRoot, fileFilter.files);

    // Requires extraction — through registry
    const requires = registry.extractRequires(kind, ref, manifest);
    acc.allEdges.push(...requires);

    acc.assets.push({
      ref,
      kind,
      publisher,
      name,
      version,
      manifest,
      manifestPath: absPath,
      relativePath,
      fileFilter,
      sha256,
      requires,
    });
  } catch (err) {
    acc.errors.push({
      path: relativePath,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

/**
 * Apply the atomization policy to a structural file filter in place.
 *
 * - `strict`: leave the byte set untouched; emit a warning per uncovered in-dir
 *   reference so the gap is visible but nothing is silently bundled.
 * - `closure`: expand the file set to the reference closure (the in-dir files
 *   the asset textually references, transitively) and emit an info warning
 *   naming each file pulled in beyond the structural defaults.
 * - `full-dir`: capture the entire asset directory, carving out any nested
 *   sub-asset subtree (a subdir with its own manifest), then warn with the
 *   delta beyond the structural filter.
 */
function applyAtomizationPolicy(
  policy: AtomizationPolicy,
  relativePath: string,
  fileFilter: AssetKindFileFilter,
  registry: IAssetKindRegistry,
  warnings: ReferenceWarning[],
): void {
  if (policy === "strict") {
    warnings.push(...lintUncoveredReferences(relativePath, fileFilter.assetRoot, fileFilter.files));
    return;
  }

  const structural = new Set(fileFilter.files);
  const expanded =
    policy === "full-dir"
      ? collectFullDirFiles(fileFilter.assetRoot, registry)
      : closeOverReferences(fileFilter.assetRoot, fileFilter.files).files;

  fileFilter.files = expanded;
  const reason =
    policy === "full-dir" ? "full-dir capture" : "reference closure (referenced but uncaptured)";
  for (const f of expanded) {
    if (!structural.has(f)) {
      warnings.push({ path: relativePath, warning: `captured "${f}" via ${reason}` });
    }
  }
}

/**
 * Collect every file under `assetRoot`, excluding subtrees rooted at a nested
 * sub-asset — any subdirectory (other than the root) that holds its own
 * manifest, which discovery splits out as a separate atom. Returns posix paths
 * relative to assetRoot, sorted.
 */
function collectFullDirFiles(assetRoot: string, registry: IAssetKindRegistry): string[] {
  const all: string[] = [];
  const nestedDirs: string[] = [];
  for (const { relativePath } of walkTree(assetRoot)) {
    all.push(relativePath);
    const dir = path.posix.dirname(relativePath);
    if (dir === ".") continue; // the asset's own manifest lives at the root
    let kind: string | undefined;
    try {
      kind = registry.resolveKind(relativePath);
    } catch {
      kind = undefined;
    }
    if (kind) nestedDirs.push(dir);
  }

  const carved = [...new Set(nestedDirs)];
  const inCarvedSubtree = (file: string) => carved.some((d) => file.startsWith(`${d}/`));
  return all.filter((f) => !inCarvedSubtree(f)).sort();
}

/**
 * Build requires edges from body links that cross into ANOTHER discovered
 * asset. Two complementary matchers, both yielding soft `body-link` edges:
 *  - path-based: a reference escaping its asset root onto another asset's root.
 *  - name-based: a standalone `/<name>` slash command matching exactly one other
 *    discovered asset (Claude-Code cross-skill refs carry no path).
 * Typed manifest fields remain the authoritative edge source; these only add
 * links the author expressed as prose/markdown references.
 */
function extractBodyLinkEdges(assets: DiscoveredAsset[]): RequiresEdge[] {
  // Index assets by absolute root, longest-first for most-specific matching.
  const roots = assets
    .map((a) => ({ root: a.fileFilter.assetRoot, ref: a.ref }))
    .sort((x, y) => y.root.length - x.root.length);

  const edges: RequiresEdge[] = [];
  const seen = new Set<string>();

  const nameToRefs = buildSkillNameToRefs(assets);

  for (const asset of assets) {
    const root = asset.fileFilter.assetRoot;
    const afs = createNodeAssetFs(root);
    for (const r of scanAssetReferences(afs, asset.fileFilter.files)) {
      if (r.classification !== "external") continue;
      const target = path.resolve(root, r.resolved);
      const hit = roots.find(
        (cand) =>
          cand.ref !== asset.ref &&
          (target === cand.root || target.startsWith(`${cand.root}${path.sep}`)),
      );
      if (hit) pushBodyLinkEdge(edges, seen, asset.ref, hit.ref);
    }
    for (const name of scanSlashCommandNames(afs, asset.fileFilter.files)) {
      const to = resolveSlashCommand(nameToRefs, name, asset.ref);
      if (to) pushBodyLinkEdge(edges, seen, asset.ref, to);
    }
  }

  return edges;
}

// ---------------------------------------------------------------------------
// Directory walker
// ---------------------------------------------------------------------------

function* walkTree(rootDir: string): Generator<{ absPath: string; relativePath: string }> {
  function* walk(
    dir: string,
    relDir: string,
  ): Generator<{ absPath: string; relativePath: string }> {
    let entries: fs.Dirent[];
    try {
      entries = fs.readdirSync(dir, { withFileTypes: true });
    } catch {
      return;
    }
    for (const e of entries.sort((a, b) => (a.name < b.name ? -1 : 1))) {
      const full = path.join(dir, e.name);
      const rel = relDir ? `${relDir}/${e.name}` : e.name;
      if (e.isDirectory()) {
        if (e.name === ".git" || e.name === "node_modules" || e.name === ".skaile") continue;
        yield* walk(full, rel);
      } else if (e.isFile()) {
        yield { absPath: full, relativePath: rel };
      }
    }
  }
  yield* walk(rootDir, "");
}

// ---------------------------------------------------------------------------
// Manifest parsing
// ---------------------------------------------------------------------------

/**
 * Parse a manifest file. .md files get frontmatter-parsed (YAML header),
 * .yaml files get full-YAML-parsed, .json files get JSON.parsed.
 */
function parseManifest(absPath: string): Record<string, unknown> | null {
  try {
    const content = fs.readFileSync(absPath, "utf-8");

    if (absPath.endsWith(".md")) {
      const { data } = parseFrontmatter(content);
      // A .md with no `---` block yields `{}`; for a kind-matched file that is a
      // parse failure, not a default-identity manifest.
      if (!data || Object.keys(data).length === 0) return null;
      return data as Record<string, unknown>;
    }

    if (absPath.endsWith(".yaml") || absPath.endsWith(".yml")) {
      const parsed = yaml.load(content);
      if (typeof parsed === "object" && parsed !== null) {
        return parsed as Record<string, unknown>;
      }
      return null;
    }

    if (absPath.endsWith(".json")) {
      return JSON.parse(content) as Record<string, unknown>;
    }

    return null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Publisher resolution
// ---------------------------------------------------------------------------

/**
 * Resolve the publisher namespace for an asset at a given relative path.
 *
 * Resolution order:
 *   1. Publisher overrides (first match wins)
 *   2. publisher_default template (<domain> replaced with first-level dir name)
 *   3. Fallback: <first-dir-component>
 *
 * The result is always **bare** (no leading `@`): the internal SourceConfig
 * carries `@`-prefixed publishers, but `DiscoveredAsset.publisher` must be bare
 * (the scope sigil is re-added by the ref formatter). See {@link barePublisher}.
 */
function resolvePublisher(relativePath: string, sourceConfig?: SourceConfig): string {
  if (!sourceConfig) {
    // No source config: derive from first directory component
    const domain = relativePath.split("/")[0] ?? "unknown";
    return domain;
  }

  // Check publisher overrides first (first match wins)
  for (const override of sourceConfig.publisher_overrides) {
    if (relativePath.startsWith(override.path)) {
      return barePublisher(override.publisher);
    }
  }

  // Use publisher_default template
  const template = sourceConfig.publisher_default;
  if (template.includes("<domain>")) {
    const domain = relativePath.split("/")[0] ?? "unknown";
    return barePublisher(template.replace("<domain>", domain));
  }
  return barePublisher(template);
}

// ---------------------------------------------------------------------------
// Name derivation
// ---------------------------------------------------------------------------

/**
 * Resolve an asset's version from its manifest. Prefers the top-level
 * `version:` field, then falls back to `metadata.version` — Claude-skill
 * `SKILL.md` frontmatter conventionally nests custom fields under `metadata:`,
 * so a version declared there was previously dropped and defaulted to "0.1.0".
 * Unquoted YAML (`version: 1.0`) parses as a JS number, so scalars are coerced
 * to strings. Defaults to "0.1.0" (not "0.0.0", which the Catalog rejects at
 * publish) when absent or non-scalar.
 */
function coerceVersion(manifest: Record<string, unknown>): string {
  const scalar = (v: unknown): string | null =>
    typeof v === "string" ? v : typeof v === "number" ? String(v) : null;
  const top = scalar(manifest.version);
  if (top !== null) return top;
  const metadata = manifest.metadata;
  if (metadata !== null && typeof metadata === "object") {
    const nested = scalar((metadata as Record<string, unknown>).version);
    if (nested !== null) return nested;
  }
  return "0.1.0";
}

/**
 * Derive the asset name. Prefers manifest frontmatter `name:` field,
 * falls back to parent directory name.
 */
function deriveName(relativePath: string, kind: string, manifest: Record<string, unknown>): string {
  // Use name from manifest only if it's a canonical asset name — an untrusted
  // name with `/`, `@`, or `..` would flow into refs/paths.
  if (
    typeof manifest.name === "string" &&
    manifest.name.length > 0 &&
    isValidAssetName(manifest.name)
  ) {
    return manifest.name;
  }

  const parts = relativePath.split("/");

  // For flow/prompt/preset, the name is the filename stem
  if (kind === "flow") {
    const filename = parts[parts.length - 1] ?? "";
    return filename.replace(/\.flow\.(yaml|json)$/, "");
  }
  if (kind === "prompt") {
    const filename = parts[parts.length - 1] ?? "";
    return filename.replace(/\.prompt\.md$/, "");
  }
  if (kind === "preset") {
    const filename = parts[parts.length - 1] ?? "";
    return filename.replace(/\.preset\.yaml$/, "");
  }

  // For most types, the parent directory name is the asset name
  if (parts.length >= 2) {
    return parts[parts.length - 2]!;
  }

  // Fallback
  const filename = parts[parts.length - 1] ?? "unknown";
  return filename.replace(/\.(md|yaml|json)$/, "");
}
